import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Skeleton } from "@/components/ui/skeleton";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  ShieldCheck, Compass, ArrowRight, ExternalLink, Headphones, Info, CheckCircle2,
  AlertTriangle, ClipboardList, Phone,
} from "lucide-react";

function Card({ title, icon: Icon, children, testid, tone }) {
  const border = tone === "navy" ? "border-[#0B192C] bg-[#0B192C] text-white" : "border-[#E5E5E0] bg-white";
  return (
    <div className={`mt-5 rounded-2xl border-2 ${border} p-5`} data-testid={testid}>
      {title && <h2 className={`font-bold flex items-center gap-2 ${tone === "navy" ? "text-[#E9C46A]" : "text-[#0B192C]"}`}>{Icon && <Icon className="w-5 h-5" />} {title}</h2>}
      {children}
    </div>
  );
}

const SHARE_TEXT = "I agree to share my full Ask Nauticus conversation with Senior Benefit Advisors FL so my licensed insurance agent has complete context.";

export default function ReviewJourney() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [home, setHome] = useState(null);
  const [avail, setAvail] = useState(null);
  const [plans, setPlans] = useState(null);
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [resumeToken, setResumeToken] = useState("");
  const [dest, setDest] = useState(null);        // {ref, resolved, ...}
  const [advisorFor, setAdvisorFor] = useState(null);  // {candidate_ref, needs_eligibility_check, plan_name}
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [contact, setContact] = useState(null);  // {phone, email, call:{label, number}}

  const loadAll = async () => {
    const [h, a, s] = await Promise.all([
      api.get("/consumer/household/home").then((r) => r.data).catch(() => null),
      api.get("/consumer/review/availability").then((r) => r.data).catch(() => null),
      api.get("/consumer/review/status").then((r) => r.data).catch(() => null),
    ]);
    setHome(h); setAvail(a); setStatus(s);
    if (h?.household_id) {
      api.get("/consumer/review/contact").then((r) => setContact(r.data)).catch(() => setContact(null));
      const p = await api.get("/consumer/review/plans").then((r) => r.data).catch(() => null);
      setPlans(p);
    }
  };
  useEffect(() => { loadAll().finally(() => setLoading(false)); }, []);

  const claim = async () => {
    if (!resumeToken.trim()) return;
    try {
      await api.post("/households/claim", { resume_token: resumeToken.trim() });
      toast.success("Welcome back — your saved information is now linked to My Nauticus.");
      setResumeToken(""); setLoading(true); await loadAll(); setLoading(false);
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
  };

  const resolveEnroll = async (ref) => {
    try {
      const { data } = await api.post("/consumer/review/enroll-destination", { candidate_ref: ref });
      setDest({ ref, ...data });
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
  };

  const openEnrollLink = async () => {
    if (!dest?.resolved) return;
    window.open(dest.destination?.url, "_blank", "noopener");
    try {
      await api.post("/consumer/review/enroll-opened", {
        candidate_ref: dest.ref, destination_url: dest.destination?.url,
        provider_code: dest.provider?.code, producer_id: dest.producer_id, fmo_id: dest.fmo_id });
      toast.success("We noted that you opened the enrollment link.");
      setDest(null); await loadAll();
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
  };

  if (loading) return <div className="p-6 max-w-3xl mx-auto space-y-4"><Skeleton className="h-24 w-full rounded-2xl" /><Skeleton className="h-64 w-full rounded-2xl" /></div>;

  const first = home?.first_name || (user?.name || "").split(" ")[0] || "there";
  const reviewYear = avail?.review_year || avail?.current_year;

  // Activation guard — no linked household yet
  if (!home?.household_id) {
    return (
      <div className="p-5 sm:p-8 max-w-xl mx-auto" data-testid="review-activate">
        <h1 className="font-display text-3xl text-[#0B192C]">Welcome back{first !== "there" ? `, ${first}` : ""}</h1>
        <p className="mt-2 text-[#4B5563]">Link what you've already saved with Senior Benefit Advisors to start your Medicare review. This connects it to your My Nauticus account — it doesn't create a new lead.</p>
        <Card title="Connect your saved information" icon={ShieldCheck} testid="review-activate-card">
          <Label className="text-xs mt-3 block">Your resume link or code</Label>
          <Input value={resumeToken} onChange={(e) => setResumeToken(e.target.value)} placeholder="Paste the link from your email" className="mt-1 h-11 bg-white" data-testid="review-resume-input" />
          <Button onClick={claim} className="mt-3 h-11 w-full bg-[#0B192C] text-white" data-testid="review-claim-btn">Activate & continue <ArrowRight className="w-4 h-4 ml-1" /></Button>
        </Card>
      </div>
    );
  }

  const cov = plans?.current || home?.current_coverage || {};
  const covVerified = cov.verified || home?.current_coverage?.state === "verified";
  const candidates = plans?.candidates || [];

  return (
    <div className="p-5 sm:p-8 max-w-3xl mx-auto" data-testid="review-journey">
      <h1 className="font-display text-3xl text-[#0B192C]">Your {reviewYear} Medicare review</h1>
      <p className="mt-1 text-[#4B5563]">A clear look at your coverage and your options, {first}. Facts only — anything we haven't checked is marked.</p>

      {/* 2027 availability gate */}
      {avail && !avail.next_year_available && (
        <div className="mt-4 rounded-xl border-2 border-[#F3E1C4] bg-[#FBF3E3] p-3 flex items-start gap-2" data-testid="review-2027-gate">
          <Info className="w-4 h-4 text-[#92400E] mt-0.5 shrink-0" />
          <p className="text-sm text-[#7A4B10]">{avail.message}</p>
        </div>
      )}

      {/* Honest next status */}
      <CallLink call={contact?.call} />
      {(status?.self_enroll || status?.advisor) && (
        <Card title="Where things stand" icon={ClipboardList} testid="review-status">
          {status.self_enroll && (
            <div className="mt-2 text-sm" data-testid="review-status-selfenroll">
              <p className="font-semibold text-[#0B192C]">Enrollment link opened on {(status.self_enroll.opened_at || "").slice(0, 10)}</p>
              <p className="text-[#92400E]">Application submission has not been confirmed.</p>
              {status.self_enroll.plan_name && <p className="text-xs text-[#6B7280] mt-0.5">{status.self_enroll.plan_name}</p>}
            </div>
          )}
          {status.advisor && (
            <div className="mt-3 text-sm" data-testid="review-status-advisor">
              <p className="font-semibold text-[#0B192C]">{status.advisor.outcome_label || "Advisor review requested"}</p>
              {status.advisor.needs_eligibility_check && <p className="text-[#92400E]">Flagged for an eligibility / contracting check before any enrollment.</p>}
            </div>
          )}
        </Card>
      )}

      {/* Confirm current plan */}
      <Card title={`Your ${avail?.current_year || ""} plan`} icon={ShieldCheck} testid="review-current">
        {covVerified ? (
          <div className="mt-3 text-sm" data-testid="review-current-verified">
            <p className="font-semibold text-[#0B192C]">{cov.carrier ? `${cov.carrier} — ` : ""}{cov.plan_name}</p>
            <p className="text-xs text-[#92400E] mt-1" data-testid="review-coverage-label">Consumer-reported coverage · not verified with the carrier</p>
          </div>
        ) : (
          <div className="mt-3" data-testid="review-current-unverified">
            <p className="text-sm text-[#4B5563]">Confirm your current plan so we can compare it to your options — no plan ID numbers needed.</p>
            <Button variant="outline" size="sm" className="mt-3" onClick={() => setConfirmOpen(true)} data-testid="review-confirm-plan">Confirm my current plan <ArrowRight className="w-3.5 h-3.5 ml-1" /></Button>
          </div>
        )}
      </Card>

      {/* Options + decision */}
      <Card title="Your options" icon={Compass} testid="review-options">
        {candidates.length === 0 ? (
          <p className="mt-3 text-sm text-[#6B7280]" data-testid="review-options-empty">No plans in your review yet. Add plans in Ask Nauticus and they'll show here.</p>
        ) : (
          <>
            <div className="mt-2 flex flex-wrap gap-1.5" data-testid="review-open-items">
              {(plans?.open_items || []).map((o) => (
                <span key={o.key} className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-[#FEF3C7] text-[#92400E]">{o.label}</span>
              ))}
            </div>
            <div className="mt-3 space-y-3">
              {candidates.map((c) => (
                <div key={c.ref} className="rounded-xl border border-[#E5E5E0] p-3" data-testid={`review-plan-${c.ref}`}>
                  <p className="font-semibold text-[#0B192C]">{c.plan_name}</p>
                  <p className="text-xs text-[#6B7280]">{[c.carrier, c.plan_type, c.snp_type, c.subtitle].filter(Boolean).join(" · ")}</p>
                  <div className="mt-3 flex flex-wrap gap-2">
                    <Button size="sm" className="bg-[#0B192C] text-white" onClick={() => resolveEnroll(c.ref)} data-testid={`review-enroll-${c.ref}`}><ExternalLink className="w-3.5 h-3.5 mr-1" />Enroll myself</Button>
                    <Button size="sm" variant="outline" onClick={() => setAdvisorFor({ candidate_ref: c.ref, needs_eligibility_check: false, plan_name: c.plan_name })} data-testid={`review-advisor-${c.ref}`}><Headphones className="w-3.5 h-3.5 mr-1" />Talk to my advisor</Button>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
      </Card>

      {/* Enroll destination result */}
      <Dialog open={!!dest} onOpenChange={(v) => !v && setDest(null)}>
        <DialogContent data-testid="review-enroll-dialog">
          <DialogHeader><DialogTitle>{dest?.plan?.plan_name}</DialogTitle>
            <DialogDescription>Review how you can move forward with this plan.</DialogDescription>
          </DialogHeader>
          {dest?.resolved ? (
            <div className="space-y-3">
              <div className="rounded-lg border border-[#CDE7D8] bg-[#F0F7F4] p-3 text-sm">
                <p className="font-bold text-[#1F3D2F] flex items-center gap-1"><CheckCircle2 className="w-4 h-4" />Online enrollment available</p>
                <p className="text-[#374151] mt-1">Through {dest.provider?.name}</p>
                {dest.destination?.description && <p className="text-[#6B7280] mt-1" data-testid="review-enroll-desc">{dest.destination.description}</p>}
              </div>
              <p className="text-xs text-[#6B7280]">Opening the link takes you to the enrollment marketplace. It does not submit an application — you complete it there.</p>
              <Button onClick={openEnrollLink} className="w-full h-11 bg-[#0B192C] text-white" data-testid="review-open-enroll-link">Open enrollment link <ExternalLink className="w-4 h-4 ml-1" /></Button>
            </div>
          ) : (
            <div className="space-y-3" data-testid="review-enroll-none">
              <div className="rounded-lg border border-[#F3D6C4] bg-[#FDF3EA] p-3 text-sm flex items-start gap-2">
                <AlertTriangle className="w-4 h-4 text-[#B45309] mt-0.5 shrink-0" />
                <p className="text-[#7A4B10]">{dest?.message || "Online enrollment for this plan isn't available through Nauticus right now."}</p>
              </div>
              <Button onClick={() => { setAdvisorFor({ candidate_ref: dest.ref, needs_eligibility_check: true, plan_name: dest?.plan?.plan_name }); setDest(null); }} className="w-full h-11 bg-[#0B192C] text-white" data-testid="review-fallback-advisor"><Headphones className="w-4 h-4 mr-1" />Talk to a licensed advisor</Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <AdvisorDialog req={advisorFor} contact={contact} onClose={() => setAdvisorFor(null)} onDone={async () => { setAdvisorFor(null); await loadAll(); }} />
      <ConfirmPlanDialog open={confirmOpen} onClose={() => setConfirmOpen(false)} onDone={async () => { setConfirmOpen(false); setLoading(true); await loadAll(); setLoading(false); }} />
    </div>
  );
}

function ConfirmPlanDialog({ open, onClose, onDone }) {
  const [carrier, setCarrier] = useState("");
  const [county, setCounty] = useState("");
  const [results, setResults] = useState(null);
  const [busy, setBusy] = useState(false);
  const search = async () => {
    if (!carrier.trim() || !county.trim()) { toast.error("Enter your carrier and county."); return; }
    setBusy(true);
    try {
      const { data } = await api.get("/consumer/review/plan-search", { params: { carrier: carrier.trim(), county: county.trim() } });
      setResults(data.plans || []);
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const confirm = async (p, replace = false) => {
    setBusy(true);
    try {
      await api.post("/consumer/review/confirm-current", { contract_id: p.contract_id, pbp: p.pbp, segment_id: p.segment_id, replace });
      toast.success("Your current plan is confirmed.");
      onDone();
    } catch (e) {
      const d = e?.response?.data?.detail;
      if (e?.response?.status === 409 && d?.code === "current_plan_conflict") {
        if (d.replaceable && window.confirm(`${d.message} Replace it with ${p.plan_name}?`)) { setBusy(false); return confirm(p, true); }
        if (!d.replaceable && window.confirm(`Your licensed insurance agent has ${d.existing?.plan_name || "a plan"} on file, so it can't be changed here. Ask your licensed insurance agent to correct it to ${p.plan_name}?`)) {
          await api.post("/consumer/ask/coverage-correction", { contract_id: p.contract_id, pbp: p.pbp, segment_id: p.segment_id, plan_name: p.plan_name });
          toast.success("We've asked your licensed insurance agent to check and correct your plan on file.");
        }
      } else toast.error(formatApiError(d));
    }
    finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent data-testid="review-confirm-dialog">
        <DialogHeader><DialogTitle>Confirm your current plan</DialogTitle>
          <DialogDescription>Find your plan by carrier and county — we verify the exact plan, no ID numbers needed.</DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-2">
          <Input placeholder="Carrier (e.g. Humana)" value={carrier} onChange={(e) => setCarrier(e.target.value)} data-testid="review-confirm-carrier" />
          <Input placeholder="County (e.g. Broward)" value={county} onChange={(e) => setCounty(e.target.value)} data-testid="review-confirm-county" />
        </div>
        <Button onClick={search} disabled={busy} variant="outline" className="w-full" data-testid="review-confirm-search">Find my plan</Button>
        {results && (
          <div className="max-h-[42vh] overflow-y-auto space-y-2 mt-1" data-testid="review-confirm-results">
            {results.length === 0 && <p className="text-sm text-[#6B7280]" data-testid="review-confirm-empty">No plans found on file for that carrier and county.</p>}
            {results.map((p) => (
              <div key={p.display_id} className="flex items-center justify-between gap-3 rounded-lg border border-[#E5E5E0] p-3" data-testid={`review-confirm-option-${p.display_id}`}>
                <div className="min-w-0">
                  <p className="font-semibold text-sm text-[#0B192C] truncate">{p.plan_name}</p>
                  <p className="text-xs text-[#6B7280] truncate">{[p.organization, p.plan_type, p.snp_type].filter(Boolean).join(" · ")}</p>
                </div>
                <Button size="sm" className="bg-[#0B192C] text-white shrink-0" disabled={busy} onClick={() => confirm(p)} data-testid={`review-confirm-select-${p.display_id}`}>This is my plan</Button>
              </div>
            ))}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

const fmtPhone = (n) => { const d = (n || "").replace(/\D/g, "").slice(-10); return d.length === 10 ? `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}` : n; };

function CallLink({ call }) {
  if (!call?.number) return null;
  return (
    <a href={`tel:${call.number}`} className="mb-4 flex items-center gap-2 rounded-lg border-2 border-[#0B192C] px-4 py-3 text-sm font-semibold text-[#0B192C] hover:bg-[#F5F5F0]" data-testid="review-call-link">
      <Phone className="w-4 h-4" /> {call.label} · {fmtPhone(call.number)}
    </a>
  );
}

function AdvisorDialog({ req, contact, onClose, onDone }) {
  const [form, setForm] = useState({ phone: "", email: "", consent: false });
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (req) { setForm((f) => ({ ...f, phone: f.phone || contact?.phone || "", email: f.email || contact?.email || "" })); }
  }, [req, contact]);
  if (!req) return null;
  const submit = async () => {
    if (!form.consent) { toast.error("Please give consent so a licensed insurance agent can contact you."); return; }
    setBusy(true);
    try {
      await api.post("/consumer/review/advisor", {
        candidate_ref: req.candidate_ref, needs_eligibility_check: req.needs_eligibility_check,
        phone: form.phone || null, email: form.email || null, consent: true });
      toast.success("Your licensed insurance agent has your review — they'll follow up.");
      onDone();
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open={!!req} onOpenChange={(v) => !v && onClose()}>
      <DialogContent data-testid="review-advisor-dialog">
        <DialogHeader><DialogTitle>Talk to a licensed insurance agent</DialogTitle>
          <DialogDescription>Share how to reach you; your review context goes with the request.</DialogDescription>
        </DialogHeader>
        {req.needs_eligibility_check && (
          <p className="text-xs text-[#7A4B10] bg-[#FDF3EA] rounded p-2" data-testid="review-advisor-eligibility-note">
            We'll route {req.plan_name} for an eligibility and contracting check. A licensed insurance agent can't enroll you in this plan until that's confirmed.
          </p>
        )}
        <p className="text-sm text-[#4B5563]">Your current coverage, the plans you're reviewing, and what we haven't yet checked go to the licensed insurance agent for context.</p>
        <div className="grid grid-cols-2 gap-3 mt-1">
          <Input placeholder="Phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="review-advisor-phone" />
          <Input placeholder="Email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="review-advisor-email" />
        </div>
        <label className="flex items-start gap-2 text-sm mt-1">
          <Checkbox checked={form.consent} onCheckedChange={(v) => setForm({ ...form, consent: !!v })} data-testid="review-advisor-consent" />
          <span>A licensed insurance agent may contact me about this request.</span>
        </label>
        <Button onClick={submit} disabled={busy} className="mt-2 h-11 bg-[#0B192C] text-white" data-testid="review-advisor-submit">Request my advisor</Button>
        <CallLink call={contact?.call} />
      </DialogContent>
    </Dialog>
  );
}
